import { LitElement, html } from 'lit';
import IMask from 'imask';

import { decimalSeparator, thousandSeparator } from '../../utils/intl';
import dispatchCustomEvent from '../../utils/dispatch-custom-event';
import translate from '../../utils/webcomponents/translate';
import { isValidTrackingObject } from '../../tracking/utils';

import '../loader';
import '../message';
import '../icon';

import portfolioEvents from './portfolio-events';
import * as portfolioApi from './portfolio-api';

const formTemplate = ({ amount = 1, purchasePrice = 0, id, gtmPushData }) => {
	const shouldTrack = isValidTrackingObject(gtmPushData);

	const labelPrice = translate('portfolio', 'purchasePrice', 'Purchase price');
	const labelAmount = translate('portfolio', 'amount', 'Amount');
	const labelCancel = translate('portfolio', 'cancel', 'Cancel');
	const labelSubmit = id
		? translate('portfolio', 'saveChanges', 'Save changes')
		: translate('portfolio', 'addToPortfolio', 'Add to portfolio');

	return html`
		<form class="form trailer-reset">
			<div class="field">
				<label
					class="field__label"
					for="price"
				>
					${labelPrice}
				</label>
				<input
					type="text"
					class="field__input"
					name="purchasePrice"
					id="price"
					value="${purchasePrice}"
					autocomplete="off"
					required
				/>
			</div>

			<div class="field">
				<label
					class="field__label"
					for="amount"
				>
					${labelAmount}
				</label>
				<input
					type="text"
					class="field__input"
					name="amount"
					id="amount"
					value="${amount}"
					autocomplete="off"
					required
				/>
			</div>

			<div class="form__footer form__footer--centered">
				<button
					type="submit"
					class="button"
					?data-gtm-click="${shouldTrack}"
					data-gtm-push="${JSON.stringify(gtmPushData)}"
				>
					${labelSubmit}
				</button>
				<button
					type="button"
					class="button button--ghost js-close"
				>
					${labelCancel}
				</button>
			</div>
		</form>
	`;
};

const succesTemplate = (isUpdate) => {
	const message = isUpdate
		? translate('portfolio', 'changesSaved', 'The changes are saved')
		: translate('portfolio', 'addedToPortfolio', 'Product added to your portfolio');

	const labelClose = translate('portfolio', 'close', 'Close');

	return html`
		<x-message
			modifier="positive"
			icon="checkmark"
			message="${message}"
		></x-message>

		<div class="form__footer form__footer--centered">
			<button
				type="button"
				class="button js-close"
			>
				${labelClose}
			</button>
		</div>
	`;
};

class PortfolioItemEditForm extends LitElement {
	static get properties() {
		return {
			isError: { type: Boolean },
			isSuccess: { type: Boolean },
			isLoading: { type: Boolean },

			options: {
				type: Object,
			},
		};
	}

	get isUpdate() {
		return Object.hasOwn(this.options, 'id');
	}

	constructor() {
		super();

		this.isError = false;
		this.isSuccess = false;
		this.isLoading = false;

		this.options = {};

		this.onFormSubmit = this.onSubmit.bind(this);
	}

	disconnectedCallback() {
		this.purchasePriceFormatted.destroy();
		this.amountFormatted.destroy();

		this.removeEventListener('submit', this.onSubmit);

		super.disconnectedCallback();
	}

	async firstUpdated() {
		this.addEventListener('submit', this.onSubmit);

		this.createFormattedInputs();
	}

	updated() {
		dispatchCustomEvent(this, 'updated');
	}

	render() {
		const errorFeedback = translate('portfolio', 'somethingWentWrong', 'Something went wrong. Please try again.');

		return html`
			<div>
				<x-loader .isLoading="${this.isLoading}"></x-loader>

				${this.isError
					? html`
							<x-message
								modifier="error"
								icon="warning"
								message="${errorFeedback}"
							></x-message>
						`
					: ''}
				${this.isSuccess ? succesTemplate(this.isUpdate) : formTemplate(this.options)}
			</div>
		`;
	}

	createFormattedInputs() {
		this.purchasePriceFormatted = new IMask(this.querySelector('#price'), {
			mask: Number,
			scale: 3,
			thousandsSeparator: thousandSeparator,
			radix: decimalSeparator,
			padFractionalZeros: true,
			signed: false,
			min: 0.01,
		});

		this.amountFormatted = new IMask(this.querySelector('#amount'), {
			mask: Number,
			scale: 0,
			thousandsSeparator: thousandSeparator,
			signed: false,
			min: 1,
		});
	}

	onSubmit(e) {
		e.preventDefault();

		this.updatePortfolioItem(e.target);
	}

	onSuccess() {
		this.isSuccess = true;

		dispatchCustomEvent(document.body, portfolioEvents.PORTFOLIO_CHANGED);
	}

	async updatePortfolioItem() {
		const purchasePrice = this.purchasePriceFormatted.typedValue;
		const amount = this.amountFormatted.typedValue;

		this.options = { ...this.options, purchasePrice, amount };

		const data = JSON.stringify(this.options);

		this.isLoading = true;
		this.isError = false;

		try {
			if (this.isUpdate) {
				await portfolioApi.update(data);
			} else {
				await portfolioApi.add(data);
			}

			this.onSuccess();
		} catch {
			this.isError = true;
		} finally {
			this.isLoading = false;
		}
	}

	createRenderRoot() {
		return this;
	}
}

customElements.define('x-portfolio-item-edit-form', PortfolioItemEditForm);

export default PortfolioItemEditForm;
